import React from 'react';
import { TableNew } from '@dynamika-shared/ui/TableNew/TableNew';
import ChevronIcon from '@dynamika-shared/ui/icons/Chevron';
import { createQCTasksTableConfig } from './QCTasksTableConfig';
import { Props, QCTaskRow, SliceState } from './types';
import { useSupportSubjectsListSlice } from '@restore/SupportSubject/SupportSubject.slice';
import { SupportQCTask } from '@dynamika-shared/types/subjects';
import {
  buildExecutionRows,
  buildGroupRow,
  buildModalityRows,
  getVisitName,
} from './qcHelpers';
import { filterByVisitId } from '../common/renderers';

export const QCTasks: React.FC<Props> = props => {
  const { clinicalTrialId, patientId, visitId, tasks, loading, error } = props;

  const state = useSupportSubjectsListSlice(s => s) as SliceState | undefined;

  const invalid = !clinicalTrialId || !patientId;
  const key = `${clinicalTrialId ?? ''}:${patientId ?? ''}`;

  const { visitDatesByKey = {} } = state ?? {};

  const tasksForVisit: SupportQCTask[] = filterByVisitId(tasks, visitId);

  const visitNameById: Record<string, string> = React.useMemo(() => {
    const visits = visitDatesByKey[key]?.visits ?? [];
    const map: Record<string, string> = {};

    for (const visit of visits) {
      if (!visit.id) continue;
      map[visit.id] = getVisitName(visit);
    }

    return map;
  }, [visitDatesByKey, key]);

  const baseCfg = createQCTasksTableConfig({
    headerBgInline: '#F6F9FF',
    rowBgInline: '#FFFFFF80',
    headerBgClass: 'bg-[#F6F9FF]',
    rowBgClass: 'bg-white',
  });

  const [expanded, setExpanded] = React.useState<Set<string>>(() => new Set());

  const toggleGroup = React.useCallback((groupKey: string) => {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(groupKey)) {
        next.delete(groupKey);
      } else {
        next.add(groupKey);
      }
      return next;
    });
  }, []);

  const columns = baseCfg.columns.map(col => {
    if (col.key !== 'actions') return col;

    return {
      ...col,
      render: (value: unknown, row: QCTaskRow, index: number) => {
        if (row.type !== 'group') {
          return col.render ? col.render(value, row, index) : null;
        }

        const isOpen = expanded.has(row.key);

        return (
          <div className='w-full flex items-center justify-end gap-1 pr-1'>
            <button
              type='button'
              onClick={e => {
                e.stopPropagation();
                toggleGroup(row.key);
              }}
              aria-expanded={isOpen}
              aria-label={isOpen ? 'Collapse' : 'Expand'}
              className='shrink-0 inline-flex items-center justify-center h-3 w-3 rounded-full hover:bg-black/5'
              title={isOpen ? 'Collapse' : 'Expand'}
            >
              <ChevronIcon
                dir={isOpen ? 'up' : 'down'}
                className='h-2 w-2 text-spanishGray transition-transform duration-200'
              />
            </button>
          </div>
        );
      },
    };
  });

  const rows: QCTaskRow[] = React.useMemo(() => {
    const result: QCTaskRow[] = [];

    for (const task of tasksForVisit) {
      const visitName =
        (visitId && visitNameById[visitId]) ||
        (task.visitId && visitNameById[task.visitId]) ||
        'Visit';

      const groupRow = buildGroupRow(task, visitName);
      const taskId = groupRow.key;

      const execRows = buildExecutionRows(taskId, task);
      const modalityRows = buildModalityRows(taskId, task);

      result.push(groupRow, ...execRows, ...modalityRows);
    }

    return result;
  }, [tasksForVisit, visitId, visitNameById]);

  const visibleRows: QCTaskRow[] = React.useMemo(() => {
    const v: QCTaskRow[] = [];
    for (const r of rows) {
      if (r.type === 'detail' && !expanded.has(r.groupKey)) continue;
      v.push(r);
    }
    return v;
  }, [rows, expanded]);

  if (invalid) {
    return null;
  }

  if (loading) {
    return <div>Loading QC tasks…</div>;
  }

  if (error) {
    return React.createElement(
      'div',
      { className: 'text-red-600' },
      'Failed to load QC tasks.'
    );
  }

  if (!rows.length) {
    return <div>No QC tasks.</div>;
  }

  return (
    <TableNew<QCTaskRow>
      tableClassName='main-table mt-0'
      rowKey='key'
      columns={columns}
      dataSource={visibleRows}
      pagination={false}
      components={baseCfg.components}
      rowClassName={baseCfg.rowClassName}
      onRow={baseCfg.onRow}
      style={{ margin: 0 }}
    />
  );
};
